/**
 * @fileoverview Contact Merge Picker
 *
 * Purpose: Searchable command-style picker the practitioner uses to choose
 * the surviving contact when merging duplicates within a single job.
 *
 * Behaviour:
 * - Excludes the current contact and any rows already in `merged` status.
 * - Renders the candidate's display name plus a faint identifier line
 *   (ABN / ACN / role badges) so the practitioner can disambiguate.
 * - `onPick` fires with the chosen `winnerId`. The caller is responsible
 *   for issuing `mergeJobContacts({ winnerId, loserId })` and closing the
 *   popover.
 */

import { useMemo, useRef, useState } from "react";
import { Merge } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";
import { Badge } from "@/ui/primitives/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { Button } from "@/ui/primitives/button";
import {
  CONTACT_TYPE_META,
  SOURCE_KIND_META,
  distinctSourceKinds,
  formatAbn,
  formatAcn,
  type JobContactRow,
} from "./types";

interface ContactMergePickerProps {
  current: JobContactRow;
  candidates: Array<JobContactRow>;
  onPick: (winnerId: JobContactRow["_id"]) => void;
  disabled?: boolean;
}

export function ContactMergePicker({
  current,
  candidates,
  onPick,
  disabled,
}: ContactMergePickerProps) {
  const [open, setOpen] = useState(false);

  // The picker is opened from inside the contact-detail Sheet. Without
  // the explicit `container`, Radix portals the popover to body — which
  // breaks scroll-wheel events because they bubble into the Sheet and
  // get absorbed.
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogContainer =
    triggerRef.current?.closest<HTMLElement>("[role='dialog']") ?? undefined;

  const eligible = useMemo(() => {
    return candidates.filter(
      (c) => c._id !== current._id && c.status !== "merged",
    );
  }, [candidates, current._id]);

  return (
    // Use Radix's default `modal` Popover (not modal={false}) so the
    // popover has a focus-managed dismissable layer — clicks inside the
    // popover stay inside, only Escape / outside-click dismisses.
    // Pattern mirrors src/components/settings/precedents/CanonicalKeyPicker.tsx.
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          size="sm"
          disabled={disabled || eligible.length === 0}
          className="h-7 shrink-0 gap-1.5 text-xs"
        >
          <Merge className="h-3.5 w-3.5" />
          Merge with…
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[420px] p-0"
        align="end"
        onWheel={(e) => e.stopPropagation()}
        container={dialogContainer}
      >
        <Command
          // Plain substring filter — feels like a regular text search
          // rather than cmdk's looser fuzzy/typo-tolerant scoring.
          filter={(value, search) => {
            const q = search.trim().toLowerCase();
            if (!q) return 1;
            return value.toLowerCase().includes(q) ? 1 : 0;
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <CommandInput placeholder="Search by name, ABN, ACN…" />
          <CommandList className="max-h-[340px] overscroll-contain">
            <CommandEmpty>No matching contacts.</CommandEmpty>
            {eligible.map((c) => {
              const TypeIcon = CONTACT_TYPE_META[c.contactType].icon;
              const kinds = distinctSourceKinds(c).slice(0, 3);
              return (
                <CommandItem
                  key={c._id}
                  value={`${c.displayName} ${c.abn ?? ""} ${c.acn ?? ""}`}
                  onSelect={() => {
                    onPick(c._id);
                    setOpen(false);
                  }}
                  className="items-start gap-2 px-3 py-2.5"
                >
                  <TypeIcon className="mt-0.5 h-4 w-4 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">
                      {c.displayName}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                      {c.acn && (
                        <span className="font-mono text-[10px] tabular-nums text-muted-foreground/80">
                          ACN {formatAcn(c.acn)}
                        </span>
                      )}
                      {c.abn && (
                        <span className="font-mono text-[10px] tabular-nums text-muted-foreground/80">
                          ABN {formatAbn(c.abn)}
                        </span>
                      )}
                      {kinds.map((k) => (
                        <Badge
                          key={k}
                          color={SOURCE_KIND_META[k].badgeColor}
                          variant="soft"
                          size="sm"
                          className="px-1 py-0 text-[10px]"
                        >
                          {SOURCE_KIND_META[k].shortLabel}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
